import type { AxiosInstance } from 'axios'
import { isAxiosError } from 'axios'

/**
 * Site location service — one service per backend resource, and the only
 * place that calls the HTTP client (React_TypeScript_Standards.md §4).
 *
 * Yards and power blocks are one SiteLocation resource told apart by a type
 * (stories #125670 data model, #121570 API). Types and statuses are lookups,
 * not code: the page draws one section per type the service returns, so a new
 * location type is a data change. Behaviour keys off the status *kind*, never
 * a type or status name or id.
 *
 * There is no endpoint yet, so the exported instance is an in-memory stand-in
 * that keeps its state for the life of the page load. That is what lets a
 * yard added in Site Setup show up in Deliveries' destination picker without a
 * refresh. Route names below are provisional until #121570 settles them.
 */

/** Behaviour keys off the kind, so it stays a fixed set in code. */
export type SiteLocationStatusKind = 'Planned' | 'Active' | 'Retired'

/** Location types come from the SiteLocationType lookup (today: Yard, Power Block). */
export type SiteLocationTypeDto = {
  id: number
  name: string
}

export type SiteLocationStatusDto = {
  id: number
  name: string
  typeId: number
  kind: SiteLocationStatusKind
}

export type SiteLocationDto = {
  id: number
  typeId: number
  name: string
  description: string | null
  statusId: number
  rowVersion: string
}

export type SiteLocationFilter = {
  typeId?: number
  kinds?: SiteLocationStatusKind[]
}

export type SiteLocationCreate = {
  typeId: number
  name: string
  description?: string
}

export type SiteLocationChanges = {
  name?: string
  description?: string | null
  rowVersion: string
}

export type SiteLocationService = {
  /** Company-wide, so callers need load it only once. */
  getTypes: (signal?: AbortSignal) => Promise<SiteLocationTypeDto[]>
  /** Company-wide, so callers need load it only once. */
  getStatuses: (
    typeId: number,
    signal?: AbortSignal,
  ) => Promise<SiteLocationStatusDto[]>
  /** Locations for the selected project, unordered — the page sorts. */
  list: (
    filter: SiteLocationFilter,
    signal?: AbortSignal,
  ) => Promise<SiteLocationDto[]>
  /** The new location starts in its type's Planned status. */
  create: (
    input: SiteLocationCreate,
    signal?: AbortSignal,
  ) => Promise<SiteLocationDto>
  update: (
    id: number,
    changes: SiteLocationChanges,
    signal?: AbortSignal,
  ) => Promise<SiteLocationDto>
  changeStatus: (
    id: number,
    statusId: number,
    rowVersion: string,
    signal?: AbortSignal,
  ) => Promise<SiteLocationDto>
}

/**
 * Someone else changed the record since it was loaded (HTTP 409 on a stale
 * rowVersion). Callers reload and tell the user.
 */
export class SiteLocationConflictError extends Error {
  constructor() {
    super('The site location was changed by someone else.')
    this.name = 'SiteLocationConflictError'
  }
}

/**
 * Case- and padding-insensitive form of a name. Names are unique per project
 * across all location types, so a yard and a block cannot both be "Yard A".
 * The form uses this for quick feedback; the API has the final say.
 */
export function normalizeLocationName(name: string): string {
  return name.trim().toLowerCase()
}

/** The status a new location of this type starts in, picked by kind. */
export function findPlannedStatus(
  statuses: SiteLocationStatusDto[],
): SiteLocationStatusDto | undefined {
  return statuses.find((status) => status.kind === 'Planned')
}

const BASE = '/api/sitelocation'

/** The real implementation, ready for the endpoint that does not exist yet. */
export function createSiteLocationService(
  client: AxiosInstance,
): SiteLocationService {
  /** A 409 on a write means a stale rowVersion. */
  async function write<T>(request: Promise<{ data: T }>): Promise<T> {
    try {
      return (await request).data
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        throw new SiteLocationConflictError()
      }
      throw error
    }
  }

  return {
    getTypes: async (signal) => {
      const response = await client.get<SiteLocationTypeDto[]>(
        `${BASE}/types`,
        { signal },
      )
      return response.data
    },
    getStatuses: async (typeId, signal) => {
      const response = await client.get<SiteLocationStatusDto[]>(
        `${BASE}/types/${typeId}/statuses`,
        { signal },
      )
      return response.data
    },
    list: async (filter, signal) => {
      const response = await client.get<SiteLocationDto[]>(BASE, {
        params: {
          typeId: filter.typeId,
          kinds: filter.kinds?.join(','),
        },
        signal,
      })
      return response.data
    },
    create: (input, signal) =>
      write(client.post<SiteLocationDto>(BASE, input, { signal })),
    update: (id, changes, signal) =>
      write(client.patch<SiteLocationDto>(`${BASE}/${id}`, changes, { signal })),
    changeStatus: (id, statusId, rowVersion, signal) =>
      write(
        client.put<SiteLocationDto>(
          `${BASE}/${id}/status`,
          { statusId, rowVersion },
          { signal },
        ),
      ),
  }
}

const YARD_TYPE_ID = 1
const POWER_BLOCK_TYPE_ID = 2

const MOCK_TYPES: SiteLocationTypeDto[] = [
  { id: YARD_TYPE_ID, name: 'Yard' },
  { id: POWER_BLOCK_TYPE_ID, name: 'Power Block' },
]

const MOCK_STATUSES: SiteLocationStatusDto[] = [
  { id: 1, name: 'Planned', typeId: YARD_TYPE_ID, kind: 'Planned' },
  { id: 2, name: 'Active', typeId: YARD_TYPE_ID, kind: 'Active' },
  { id: 3, name: 'Reclaimed', typeId: YARD_TYPE_ID, kind: 'Retired' },
  { id: 4, name: 'Not Started', typeId: POWER_BLOCK_TYPE_ID, kind: 'Planned' },
  { id: 5, name: 'Staged', typeId: POWER_BLOCK_TYPE_ID, kind: 'Active' },
  { id: 6, name: 'In Build', typeId: POWER_BLOCK_TYPE_ID, kind: 'Active' },
  { id: 7, name: 'Complete', typeId: POWER_BLOCK_TYPE_ID, kind: 'Retired' },
]

/** [typeId, name, statusId] */
const MOCK_SEED: [number, string, number][] = [
  [YARD_TYPE_ID, 'Yard A', 2],
  [YARD_TYPE_ID, 'Yard B', 2],
  [YARD_TYPE_ID, 'Yard C', 2],
  [YARD_TYPE_ID, 'Yard D', 2],
  [YARD_TYPE_ID, 'Yard E', 2],
  [YARD_TYPE_ID, 'Yard F', 1],
  [YARD_TYPE_ID, 'Yard N', 3],
  [POWER_BLOCK_TYPE_ID, 'Block 1', 7],
  [POWER_BLOCK_TYPE_ID, 'Block 2', 7],
  [POWER_BLOCK_TYPE_ID, 'Block 12', 6],
  [POWER_BLOCK_TYPE_ID, 'Block 15', 6],
  [POWER_BLOCK_TYPE_ID, 'Block 25', 5],
  [POWER_BLOCK_TYPE_ID, 'Block 33', 4],
  [POWER_BLOCK_TYPE_ID, 'Block 36', 4],
]

/**
 * Stand-in until the endpoint exists. Committed rather than a gitignored
 * playground: Deliveries' destination picker needs locations on a fresh clone
 * and in CI. State lives in module scope so every page sees the same list.
 * Like the API will, it rejects a write against a stale rowVersion.
 */
export function createMockSiteLocationService(): SiteLocationService {
  let nextId = 1
  const locations = new Map<number, SiteLocationDto>()

  function store(location: Omit<SiteLocationDto, 'rowVersion'>, version = 1) {
    const stored = { ...location, rowVersion: String(version) }
    locations.set(stored.id, stored)
    return { ...stored }
  }

  for (const [typeId, name, statusId] of MOCK_SEED) {
    store({ id: nextId++, typeId, name, description: null, statusId })
  }

  function current(id: number, rowVersion: string): SiteLocationDto {
    const existing = locations.get(id)
    if (!existing) throw new Error(`Site location ${id} not found`)
    if (existing.rowVersion !== rowVersion) throw new SiteLocationConflictError()
    return existing
  }

  function bump(location: SiteLocationDto): SiteLocationDto {
    return store(location, Number(location.rowVersion) + 1)
  }

  return {
    getTypes: async () => MOCK_TYPES.map((type) => ({ ...type })),
    getStatuses: async (typeId) =>
      MOCK_STATUSES.filter((status) => status.typeId === typeId).map(
        (status) => ({ ...status }),
      ),
    list: async ({ typeId, kinds }) =>
      [...locations.values()]
        .filter((location) => typeId === undefined || location.typeId === typeId)
        .filter(
          (location) =>
            !kinds ||
            kinds.includes(
              MOCK_STATUSES.find((status) => status.id === location.statusId)!
                .kind,
            ),
        )
        .map((location) => ({ ...location })),
    create: async (input) => {
      const planned = findPlannedStatus(
        MOCK_STATUSES.filter((status) => status.typeId === input.typeId),
      )
      if (!planned) throw new Error(`Unknown site location type ${input.typeId}`)
      return store({
        id: nextId++,
        typeId: input.typeId,
        name: input.name,
        description: input.description ?? null,
        statusId: planned.id,
      })
    },
    update: async (id, { rowVersion, ...changes }) =>
      bump({ ...current(id, rowVersion), ...changes }),
    changeStatus: async (id, statusId, rowVersion) =>
      bump({ ...current(id, rowVersion), statusId }),
  }
}

// Swap for `createSiteLocationService(apiClient)` when the endpoint exists.
// Nothing outside this file should need to change.
export const siteLocationService: SiteLocationService =
  createMockSiteLocationService()
